import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faFileWord,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { formatRelativeTime } from './utils/sessions';
import {
  Box,
  Tooltip,
  IconButton,
  Divider,
  Typography,
  Skeleton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';

export const SidebarContent = ({
  isExpanded,
  sessions,
  sessionGroups,
  activeSessionId,
  isLoadingSessions,
  onToggle,
  onNewSession,
  onSelectSession,
}) => {
  return (
    <Box className="sessionSidebarContent">
      {/* ── Header: toggle + new session ── */}
      <Box className={`sessionSidebarHeader ${isExpanded ? 'expanded' : 'collapsed'}`}>
        {isExpanded ? (
          <>
            <Tooltip title="New session" placement="right">
              <IconButton onClick={onNewSession} size="small" aria-label="New session">
                <FontAwesomeIcon icon={faPlus} size="sm" />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onToggle} size="small" aria-label="Collapse sidebar">
              <FontAwesomeIcon icon={faChevronLeft} size="sm" />
            </IconButton>
          </>
        ) : (
          <Tooltip title="Expand sidebar" placement="right">
            <IconButton onClick={onToggle} size="small" aria-label="Expand sidebar">
              <FontAwesomeIcon icon={faChevronRight} size="sm" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Divider />

      {/* ── Collapsed: icon-only new-session button ── */}
      {!isExpanded && (
        <Box className="sessionSidebarCollapsedActions">
          <Tooltip title="New session" placement="right">
            <IconButton onClick={onNewSession} size="small" aria-label="New session">
              <FontAwesomeIcon icon={faPlus} size="sm" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* ── Session history list (scrollable) ── */}
      <Box className="sessionSidebarListRegion">
        {isLoadingSessions ? (
          <Box className={`sessionSidebarSkeletonWrapper ${isExpanded ? 'expanded' : 'collapsed'}`}>
            {[...Array(6)].map((_, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                className={`sessionSidebarSkeleton ${isExpanded ? 'expanded' : 'collapsed'}`}
              />
            ))}
          </Box>
        ) : sessions.length === 0 ? (
          isExpanded && (
            <Box className="sessionSidebarEmptyState">
              <Typography
                variant="body2"
                color="text.secondary"
                className="sessionSidebarEmptyTitle"
              >
                No sessions yet.
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                className="sessionSidebarEmptyHint"
              >
                Upload a document to get started.
              </Typography>
            </Box>
          )
        ) : (
          Object.entries(sessionGroups).map(([groupLabel, groupSessions]) => {
            if (groupSessions.length === 0) return null;
            return (
              <Box key={groupLabel} className="sessionSidebarGroup">
                {isExpanded && (
                  <Typography variant="overline" className="sessionSidebarGroupLabel">
                    {groupLabel}
                  </Typography>
                )}

                <List disablePadding dense>
                  {groupSessions.map((session) => {
                    const sessionId = session.SESSION_ID;
                    const isActive = sessionId === activeSessionId;
                    const documentName = session.DOC_NAME;
                    const timeLabel = formatRelativeTime(session.UPDATED_AT);

                    const item = (
                      <ListItemButton
                        selected={isActive}
                        onClick={() => onSelectSession(sessionId)}
                        aria-label={documentName}
                        className={`sessionSidebarItem ${isExpanded ? 'expanded' : 'collapsed'} ${
                          isActive ? 'active' : ''
                        }`}
                      >
                        <ListItemIcon
                          className={`sessionSidebarItemIcon ${isExpanded ? 'expanded' : 'collapsed'} ${
                            isActive ? 'active' : 'inactive'
                          }`}
                        >
                          <FontAwesomeIcon icon={faFileWord} size="sm" />
                        </ListItemIcon>

                        <ListItemText
                          primary={documentName}
                          secondary={timeLabel}
                          primaryTypographyProps={{
                            noWrap: true,
                            className: `sessionSidebarItemPrimary ${isActive ? 'active' : ''}`,
                          }}
                          secondaryTypographyProps={{
                            noWrap: true,
                            className: 'sessionSidebarItemSecondary',
                          }}
                        />
                      </ListItemButton>
                    );

                    return (
                      isExpanded && (
                        <Tooltip key={sessionId} title={documentName} placement="right">
                          {item}
                        </Tooltip>
                      )
                    );
                  })}
                </List>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};
